import {
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { bodyToConfig, CommonOption, Device } from '@data';
import {
  ButtonComponent,
  DURATIONS_UNITS,
  IconComponent,
  LoaderWrapperComponent,
  PRIORITY,
  REFERENCE_VALUE_UNITS,
  SYMBOLS_FILTER_OPERATORS,
  VALUE_PERCENTAGE,
} from '@ui';
import { SelectOption } from '@form';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NgClass } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdministrationService } from '../../../../app/services/administration.service';
import { TenantsService } from '../../../../app/core/services/tenants.service';
import { NzTableModule } from 'ng-zorro-antd/table';
import { LIST_TABLES_DRAWER_ADMIN } from '../../../../data/constants/mocked-data.constants';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { PermissionsService } from '../../../../app/core/services/permissions.service';

@Component({
  selector: 'ui-administration-drawer',
  imports: [
    NzButtonModule,
    NzDrawerModule,
    NzSpaceModule,
    ButtonComponent,
    TranslocoDirective,
    IconComponent,
    ReactiveFormsModule,
    NzCheckboxModule,
    NzFormModule,
    NzInputModule,
    NzSelectModule,
    NgClass,
    NzTableModule,
    FormsModule,
    LoaderWrapperComponent,
  ],
  template: `
    <div *transloco="let t">
      <nz-drawer
        [nzVisible]="visible()"
        [nzClosable]="false"
        nzPlacement="right"
        [nzTitle]="extra"
        [nzWidth]="'80%'"
        nzMaskClosable="false"
        [nzFooter]="footerTpl"
        (nzOnClose)="close()"
      >
        <ng-container *nzDrawerContent>
          @if (loading()) {
            <ui-loader [loading]="loading()" />
          } @else {
            <form
              nz-form
              [formGroup]="adminAlarmForm"
              id="adminAlarmForm"
              class="grid grid-cols-12 gap-x-3 gap-y-3"
            >
              @if (this.typeOfView() !== 'edit_center' && this.typeOfView() !== 'view-saved') {
                <nz-form-item class="col-span-6 block mb-1">
                  <nz-form-label
                    nzFor="distributor"
                    class="font-bold text-[16px]"
                    ><span class="text-red-600 mr-1">*</span
                    >{{ t('ADMIN.TENANT') }}</nz-form-label
                  >
                  <nz-form-control>
                    <nz-select
                      nzShowSearch
                      [nzDisabled]="typeOfView() === 'edit_center'"
                      required
                      id="distributor"
                      formControlName="distributor"
                      [nzSize]="'large'"
                      (ngModelChange)="distributorChange()"
                      [nzPlaceHolder]="t('ADMIN.SELECT_TENANT')"
                      [ngClass]="getSelectClass('distributor')"
                    >
                      @for (tenant of sortedTenants(); track tenant.value) {
                        <nz-option
                          [nzValue]="tenant.value"
                          [nzLabel]="tenant.label"
                        ></nz-option>
                      }
                    </nz-select>
                  </nz-form-control>
                  @if (adminAlarmForm.get('distributor')?.invalid && adminAlarmForm.get('distributor')?.touched) {
                    <div class="text-red-600 text-xs mt-1">{{ t('ADMIN.REQUIRED') }}</div>
                  }
                </nz-form-item>
              }
              @if (typeOfView() === 'add_center') {
                <nz-form-item class="col-span-4 block col-start-1 mb-1">
                  <nz-form-label nzFor="center" class="font-bold text-[16px]"
                    ><span class="text-red-600 mr-1">*</span
                    >{{ t('ADMIN.CENTER') }}</nz-form-label
                  >
                  <nz-form-control>
                    <nz-select
                      nzShowSearch
                      required
                      id="center"
                      formControlName="center"
                      [nzSize]="'large'"
                      (ngModelChange)="centerChange()"
                      [nzPlaceHolder]="t('ADMIN.SELECT_CENTER')"
                      [ngClass]="getSelectClass('center')"
                    >
                      @for (center of centersByTenant(); track center.id) {
                        <nz-option
                          [nzValue]="center.id"
                          [nzLabel]="formatPair(center.id, center.name)"
                        ></nz-option>
                      }
                    </nz-select>
                  </nz-form-control>
                  @if (adminAlarmForm.get('center')?.invalid && adminAlarmForm.get('center')?.touched) {
                    <div class="text-red-600 text-xs mt-1">{{ t('ADMIN.REQUIRED') }}</div>
                  }
                </nz-form-item>
                @if (this.typeOfView() !== 'edit_center') {
                  <nz-form-item class="col-span-4 block mb-1">
                    <nz-form-label
                      nzFor="trafo"
                      class="font-bold text-[16px]"
                      >{{ t('ADMIN.TRAFO') }}</nz-form-label
                    >
                    <nz-form-control>
                      <nz-select
                        nzShowSearch
                        id="trafo"
                        formControlName="trafo"
                        [nzSize]="'large'"
                        (ngModelChange)="trafoChange()"
                        [nzPlaceHolder]="t('ADMIN.SELECT_TRAFO')"
                        [ngClass]="getSelectClass('trafo')"
                      >
                        @for (trafo of trafosByCenter(); track trafo.id) {
                          <nz-option
                            [nzValue]="trafo.id"
                            [nzLabel]="formatPair(trafo.id, trafo.name)"
                          ></nz-option>
                        }
                      </nz-select>
                    </nz-form-control>
                  </nz-form-item>

                  @if (this.deviceType() === 'tsabt') {
                    <nz-form-item class="col-span-4 block mb-1">
                      <nz-form-label
                        nzFor="line"
                        class="font-bold text-[16px]"
                        >{{ t('ADMIN.LINE') }}</nz-form-label
                      >
                      <nz-form-control>
                        <nz-select
                          nzShowSearch
                          id="line"
                          formControlName="line"
                          [nzSize]="'large'"
                          [nzPlaceHolder]="t('ADMIN.SELECT_LINE')"
                          [ngClass]="getSelectClass('line')"
                        >
                          @for (line of linesByTrafo(); track line.value) {
                            <nz-option
                              [nzValue]="line.value"
                              [nzLabel]="formatPair(line.value, line.label)"
                            ></nz-option>
                          }
                        </nz-select>
                      </nz-form-control>
                    </nz-form-item>
                  }
                }
              }

              @for (
                alarmCategory of listOfData()[deviceType()];
                track alarmCategory.alarm_category;
                let element = $index
              ) {
                <div class="col-span-12" [ngClass]="{ 'alarmCategoryContainer': typeOfView() === 'view' || typeOfView() === 'view-saved'}">
                  <div class="grid grid-cols-12 gap-x-3 gap-y-3">
                    <div class="col-span-12">
                      <h4 class="font-bold text-[16px]">
                        {{ t(alarmCategory.alarm_category) }}
                      </h4>
                    </div>
                    <div formArrayName="config" class="col-span-12">
                      <nz-table
                        #basicTable
                        [nzFrontPagination]="false"
                        [nzData]="alarmCategory.types"
                      >
                        <thead>
                          <tr>
                            <th nzWidth="255px">
                              {{ t('ADMIN.TABLE_SETTINGS.COLUMN_1') }}
                            </th>
                            <th nzWidth="162px">
                              {{ t('ADMIN.TABLE_SETTINGS.COLUMN_2') }}
                            </th>
                            <th nzWidth="320px">
                              {{ t('ADMIN.TABLE_SETTINGS.COLUMN_3') }}
                            </th>
                            <th nzWidth="122px">
                              {{ t('ADMIN.TABLE_SETTINGS.COLUMN_4') }}
                            </th>
                            <th nzWidth="96px">
                              {{ t('ADMIN.TABLE_SETTINGS.COLUMN_5') }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (
                            data of basicTable.data;
                            track data.deviceType +
                              '_' +
                              data.alarm_category +
                              '_' +
                              data.key;
                            let i = $index
                          ) {
                            <tr [formGroupName]="i + element">
                              <td class="place-content-start">
                                <div class="flex flex-wrap">
                                  {{ t(data.alarmType) }}
                                  <span class="text-red-600 ml-1">*</span>
                                </div>
                              </td>
                              <td class="place-content-start">
                                @if (data.hasReference === true) {
                                  <div class="flex flex-wrap">
                                    <nz-input-group
                                      nzSize="large"
                                      [nzAddOnAfter]="data.referenceValueUnit === 'a' ? 'A' : data.referenceValueUnit === 'C' ? 'ºC' : data.referenceValueUnit === 'v' ? 'V' : data.referenceValueUnit === 'hz' ? 'Hz' : '%'"
                                    >
                                      <input
                                        nzSize="large"
                                        type="number"
                                        class="!text-center placeholder:text-center"
                                        formControlName="referenceValue"
                                        nz-input
                                        required
                                        [ngClass]="getConfigControlClass(i + element,'referenceValue')"
                                        (keydown)="preventDot($event)"
                                        (paste)="onPasteNumeric($event, i + element, 'referenceValue')"
                                      />
                                    </nz-input-group>
                                    @if (configArray.at(i + element).get('referenceValue')?.invalid && configArray.at(i + element).get('referenceValue')?.touched) {
                                      <div class="text-red-600 text-xs mt-1 w-full">{{ t('ADMIN.REQUIRED') }}</div>
                                    }
                                  </div>
                                } @else{
                                  <div class="grid justify-center content-center h-[40px] rounded-xs border-solid border border-[#d9d9d9] noReferenceInput">
                                    {{
                                      data.referenceValueUnit === 'a' ? 'A' :
                                      data.referenceValueUnit === 'C' ? 'ºC' :
                                      data.referenceValueUnit === 'v' ? 'V' :
                                      data.referenceValueUnit === 'hz' ? 'Hz' : '%'
                                    }}
                                  </div>
                                }
                              </td>
                              <td class="place-content-start">
                                @if (!data.isRanged === true) {
                                    <div class="flex flex-wrap">
                                      <nz-input-group
                                        nzSize="large"
                                        [nzAddOnBefore]="addOnBeforeTemplateValueMin"
                                        [nzAddOnAfter]="addOnAfterTemplateValueMin"
                                      >
                                        <input
                                          nzSize="large"
                                          type="number"
                                          class="!text-center placeholder:text-center"
                                          formControlName="value_min"
                                          nz-input
                                          [ngClass]="getConfigControlClass(i + element,'value_min')"
                                          (keydown)="preventDot($event)"
                                          (paste)="onPasteNumeric($event, i + element, 'value_min')"
                                        />
                                      </nz-input-group>
                                      @if (configArray.at(i + element).get('value_min')?.invalid && configArray.at(i + element).get('value_min')?.touched) {
                                        <div class="text-red-600 text-xs mt-1">{{ t('ADMIN.REQUIRED') }}</div>
                                      }
                                      <ng-template #addOnBeforeTemplateValueMin>
                                        <nz-select
                                          nzSize="large"
                                          formControlName="valueMinOperator"
                                        >
                                          @for (
                                            option of valueOperatorOptions();
                                            track option.value
                                          ) {
                                            <nz-option
                                              [nzValue]="option.value"
                                              [nzLabel]="t(option.label)"
                                              [nzDisabled]="option.disabled || false"
                                            ></nz-option>
                                          }
                                        </nz-select>
                                      </ng-template>
                                      <ng-template #addOnAfterTemplateValueMin>
                                        {{ data.valuePercentage === true ? '%' : (
                                          data.referenceValueUnit === 'a' ? 'A' :
                                          data.referenceValueUnit === 'C' ? 'ºC' :
                                          data.referenceValueUnit === 'v' ? 'V' :
                                          data.referenceValueUnit === 'hz' ? 'Hz' : '%')
                                         }}
                                      </ng-template>
                                    </div>
                                  } @else {
                                    <div class="flex">
                                      <div class="flex flex-wrap flex-1">
                                        <nz-input-group
                                          nzSize="large"
                                          [nzAddOnBefore]="addOnBeforeTemplateValueMin"
                                        >
                                          <input
                                            nzSize="large"
                                            type="number"
                                            class="!text-center placeholder:text-center"
                                            formControlName="value_min"
                                            nz-input
                                            [ngClass]="getConfigControlClass(i + element,'value_min')"
                                            (keydown)="preventDot($event)"
                                            (paste)="onPasteNumeric($event, i + element, 'value_min')"
                                          />
                                        </nz-input-group>
                                        @if (configArray.at(i + element).get('value_min')?.invalid && configArray.at(i + element).get('value_min')?.touched) {
                                          <div class="text-red-600 text-xs mt-1">{{ t('ADMIN.REQUIRED') }}</div>
                                        }
                                      </div>
                                      <div class="flex text-center mx-2 items-center flex-none">
                                        -
                                      </div>
                                      <div class="flex flex-wrap flex-1">
                                        <nz-input-group
                                          nzSize="large"
                                          [nzAddOnBefore]="addOnBeforeTemplateValueMax"
                                          [nzAddOnAfter]="addOnAfterTemplateValueMax"
                                        >
                                          <input
                                            nzSize="large"
                                            type="number"
                                            class="!text-center placeholder:text-center"
                                            formControlName="valueMax"
                                            nz-input
                                            [ngClass]="getConfigControlClass(i + element,'valueMax')"
                                            (keydown)="preventDot($event)"
                                            (paste)="onPasteNumeric($event, i + element, 'valueMax')"
                                          />
                                        </nz-input-group>
                                        @if (configArray.at(i + element).get('valueMax')?.invalid && configArray.at(i + element).get('valueMax')?.touched) {
                                          <div class="text-red-600 text-xs mt-1">{{ t('ADMIN.REQUIRED') }}</div>
                                        }
                                      </div>
                                      <ng-template #addOnBeforeTemplateValueMin>
                                        <nz-select
                                          nzSize="large"
                                          formControlName="valueMinOperator"
                                        >
                                          @for (
                                            option of valueOperatorOptions();
                                            track option.value
                                          ) {
                                            <nz-option
                                              [nzValue]="option.value"
                                              [nzLabel]="t(option.label)"
                                              [nzDisabled]="option.disabled || false"
                                            ></nz-option>
                                          }
                                        </nz-select>
                                      </ng-template>
                                      <ng-template #addOnBeforeTemplateValueMax>
                                        <nz-select
                                          nzSize="large"
                                          formControlName="valueMaxOperator"
                                        >
                                          @for (
                                            option of valueOperatorOptions();
                                            track option.value
                                          ) {
                                            <nz-option
                                              [nzValue]="option.value"
                                              [nzLabel]="t(option.label)"
                                              [nzDisabled]="option.disabled || false"
                                            ></nz-option>
                                          }
                                        </nz-select>
                                      </ng-template>
                                      <ng-template #addOnAfterTemplateValueMax>
                                        {{ data.valuePercentage === true ? '%' : (
                                          data.referenceValueUnit === 'a' ? 'A' :
                                          data.referenceValueUnit === 'C' ? 'ºC' :
                                          data.referenceValueUnit === 'v' ? 'V' :
                                          data.referenceValueUnit === 'hz' ? 'Hz' : '%')
                                         }}
                                      </ng-template>
                                    </div>
                                  }
                              </td>
                              <td class="place-content-start">
                                <div class="flex flex-wrap">
                                  <nz-input-group
                                    nzSize="large"
                                    [nzAddOnAfter]="addOnAfterTemplateDurationUnit"
                                  >
                                    <input
                                      nzSize="large"
                                      type="number"
                                      class="!text-center placeholder:text-center"
                                      formControlName="duration"
                                      nz-input
                                      [ngClass]="getConfigControlClass(i + element,'duration')"
                                      (keydown)="preventDot($event)"
                                      (paste)="onPasteNumeric($event, i + element, 'duration')"
                                    />
                                  </nz-input-group>
                                  @if (configArray.at(i + element).get('duration')?.invalid && configArray.at(i + element).get('duration')?.touched) {
                                    <div class="text-red-600 text-xs mt-1">{{ t('ADMIN.REQUIRED') }}</div>
                                  }
                                  <ng-template #addOnAfterTemplateDurationUnit>
                                    <nz-select
                                      nzSize="large"
                                      formControlName="durationUnit"
                                    >
                                      @for (
                                        option of durationUnitOptions();
                                        track option.value
                                      ) {
                                        <nz-option
                                          [nzValue]="option.value"
                                          [nzLabel]="t(option.label)"
                                          [nzDisabled]="option.disabled || false"
                                        ></nz-option>
                                      }
                                    </nz-select>
                                  </ng-template>
                                </div>
                              </td>
                              <td class="place-content-start">
                                <nz-select
                                  nzSize="large"
                                  formControlName="priority"
                                  class="w-full"
                                >
                                  @for (
                                    option of priorityOptions();
                                    track option.value
                                  ) {
                                    <nz-option
                                      [nzValue]="option.value"
                                      [nzLabel]="t(option.label)"
                                      [nzDisabled]="option.disabled || false"
                                    ></nz-option>
                                  }
                                </nz-select>
                              </td>
                            </tr>
                          }
                        </tbody>
                      </nz-table>
                    </div>
                  </div>
                </div>
              }
            </form>
          }
        </ng-container>
      </nz-drawer>

      <!-- Header Drawer -->
      <ng-template #extra>
        <div class="flex justify-between gap-2">
          @if (typeOfView() === 'add_center') {
            <h4 class="text-[16px] font-bold">
              {{ t('ADMIN.ADD_CENTER_TITLE') }}
            </h4>
          } @else {
            <h4 class="text-[16px] font-bold">
              {{ t('ADMIN.CARD_SETTINGS_TITLE') }} {{ t(titleDevice()) }}
            </h4>
          }
          <button (click)="close()"><ui-icon icon="close" /></button>
        </div>
      </ng-template>

      <!-- Footer Drawer -->
      <ng-template #footerTpl>
        <div class="flex justify-end gap-x-2">
          @if (typeOfView() === 'edit') {
            <ui-button
              classes="w-full"
              buttonType="default"
              buttonSize="default"
              buttonShape="round"
              [loading]="false"
              [disabled]="false"
              (buttonClick)="resetValues()"
              [class]="'bg-white border border-gray-300'"
            >
              <ui-icon icon="rollback" />
              {{ t('ADMIN.RESET_VALUES') }}
            </ui-button>
            @if (hasDefaultConfig()) {
              <ui-button
                classes="w-full"
                buttonType="default"
                buttonSize="default"
                buttonShape="round"
                [loading]="false"
                [disabled]="isFormInvalid()"
                (buttonClick)="submitForm('saveGeneralSettings')"
                [class]="'bg-white border border-gray-300'"
              >
                <ui-icon icon="save" />
                {{ t('ADMIN.SAVE_CHANGES') }}
              </ui-button>
            } @else {
              <ui-button
                classes="w-full"
                buttonType="default"
                buttonSize="default"
                buttonShape="round"
                [loading]="false"
                [disabled]="isFormInvalid()"
                (buttonClick)="submitForm('addGeneralSettings')"
                [class]="'bg-white border border-gray-300'"
              >
                <ui-icon icon="save" />
                {{ t('ADMIN.ADD_DEFAULT_CONFIG') }}
              </ui-button>
            }
          }
          @if(hasWritePermission()){
            @if (typeOfView() === 'add_center') {
              <ui-button
                classes="w-full"
                buttonType="default"
                buttonSize="default"
                buttonShape="round"
                [loading]="false"
                [disabled]="isFormInvalid()"
                (buttonClick)="submitForm('saveCenter')"
                [class]="'bg-white border border-gray-300'"
              >
                <ui-icon icon="plus-circle" />
                {{ t('ADMIN.SAVE_ADD_CENTER') }}
              </ui-button>
            }

            @if (typeOfView() === 'edit_center') {
              <ui-button
                classes="w-full"
                buttonType="default"
                buttonSize="default"
                buttonShape="round"
                [loading]="false"
                [disabled]="isFormInvalid()"
                (buttonClick)="submitForm('editCenter')"
                [class]="'bg-white border border-gray-300'"
              >
                <ui-icon icon="plus-circle" />
                {{ t('ADMIN.EDIT_ADD_CENTER') }}
              </ui-button>
            }
          }
        </div>
      </ng-template>
    </div>
  `,
})
export class AdministrationDrawerComponent implements OnInit {
  protected readonly administrationService = inject(AdministrationService);
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly tenantsService = inject(TenantsService);
  private modal = inject(NzModalService);
  private transloco = inject(TranslocoService);
  private notification = inject(NzNotificationService);
  protected readonly permissionsService = inject(PermissionsService);
  protected readonly listDefault = Object.freeze({
    rsabt: [
      {
        alarm_category_Id: 'groundLeakage',
        alarm_category: 'ALARMS.CATEGORY.GROUND_LEAKAGE',
        types: [
          {
            key: 'leakageCurrentDetection',
            referenceValue: 0,
            referenceValueUnit: 'a',
            alarmType: 'ALARMS.CATEGORY.LEAKAGE_CURRENT_DETECTION',
            duration: 1,
            durationUnit: 'hour',
            priority: 'medium',
            valueMinOperator: 'less',
            value_min: 5,
            valueMaxOperator: 'more',
            valueMax: 0,
            valuePercentage: false,
            deviceType: 'rsabt',
            alarm_category: 'groundLeakage',
            isRanged: false,
            hasReference: false,
          },
        ],
      },
      {
        alarm_category_Id: 'powerQuality',
        alarm_category: 'ALARMS.CATEGORY.POWER_QUALITY',
        types: [
          {
            key: 'powerFailureVoltage',
            referenceValue: 230,
            referenceValueUnit: 'v',
            alarmType: 'ALARMS.CATEGORY.POWER_FAILURE_VOLTAGE',
            duration: 1,
            durationUnit: 'hour',
            priority: 'low',
            valueMinOperator: 'lessEquals',
            value_min: 10,
            valueMaxOperator: 'more',
            valueMax: 0,
            valuePercentage: true,
            deviceType: 'rsabt',
            alarm_category: 'powerQuality',
            isRanged: false,
            hasReference: true,
          },
          {
            key: 'voltageDrop',
            referenceValue: 230,
            referenceValueUnit: 'v',
            alarmType: 'ALARMS.CATEGORY.VOLTAGE_DROP',
            duration: 1,
            durationUnit: 'hour',
            priority: 'high',
            valueMinOperator: 'lessEquals',
            value_min: 90,
            valueMaxOperator: 'more',
            valueMax: 0,
            valuePercentage: true,
            deviceType: 'rsabt',
            alarm_category: 'powerQuality',
            isRanged: false,
            hasReference: true,
          },
          {
            key: 'overVoltage',
            referenceValue: 230,
            referenceValueUnit: 'v',
            alarmType: 'ALARMS.CATEGORY.OVERVOLTAGE',
            duration: 1,
            durationUnit: 'hour',
            priority: 'high',
            valueMinOperator: 'moreEquals',
            value_min: 110,
            valueMaxOperator: 'more',
            valueMax: 0,
            valuePercentage: true,
            deviceType: 'rsabt',
            alarm_category: 'powerQuality',
            isRanged: false,
            hasReference: true,
          },
          {
            key: 'frequency',
            referenceValue: 0,
            referenceValueUnit: 'hz',
            alarmType: 'ALARMS.CATEGORY.FREQUENCY',
            duration: 1,
            durationUnit: 'hour',
            priority: 'medium',
            valueMinOperator: 'less',
            value_min: 49,
            valueMaxOperator: 'more',
            valueMax: 51,
            valuePercentage: false,
            deviceType: 'rsabt',
            alarm_category: 'powerQuality',
            isRanged: true,
            hasReference: false,
          },
          {
            key: 'criticFrequency',
            referenceValue: 0,
            referenceValueUnit: 'hz',
            alarmType: 'ALARMS.CATEGORY.CRITIC_FREQUENCY',
            duration: 1,
            durationUnit: 'hour',
            priority: 'high',
            valueMinOperator: 'less',
            value_min: 47.5,
            valueMaxOperator: 'more',
            valueMax: 52,
            valuePercentage: false,
            deviceType: 'rsabt',
            alarm_category: 'powerQuality',
            isRanged: true,
            hasReference: false,
          },
        ],
      },
    ],
    tsabt: [
      {
        alarm_category_Id: 'blownFuse',
        alarm_category: 'ALARMS.CATEGORY.BLOWN_FUSE',
        types: [
          {
            key: 'openFuse',
            referenceValue: 250,
            referenceValueUnit: 'a',
            alarmType: 'ALARMS.CATEGORY.OPEN_FUSE',
            duration: 1,
            durationUnit: 'hour',
            priority: 'medium',
            valueMinOperator: 'lessEquals',
            value_min: 60,
            valueMaxOperator: 'more',
            valueMax: 0,
            valuePercentage: true,
            deviceType: 'tsabt',
            alarm_category: 'blownFuse',
            isRanged: false,
            hasReference: true,
          },
          {
            key: 'fuseBlownByShortCircuit',
            referenceValue: 250,
            referenceValueUnit: 'a',
            alarmType: 'ALARMS.CATEGORY.FUSE_BLOWN_BY_SHORT_CIRCUIT',
            duration: 1,
            durationUnit: 'hour',
            priority: 'medium',
            valueMinOperator: 'moreEquals',
            value_min: 250,
            valueMaxOperator: 'more',
            valueMax: 0,
            valuePercentage: true,
            deviceType: 'tsabt',
            alarm_category: 'blownFuse',
            isRanged: false,
            hasReference: true,
          },
          {
            key: 'fuseBlownByOverload',
            referenceValue: 250,
            referenceValueUnit: 'a',
            alarmType: 'ALARMS.CATEGORY.FUSE_BLOWN_BY_OVERLOAD',
            duration: 1,
            durationUnit: 'hour',
            priority: 'medium',
            valueMinOperator: 'moreEquals',
            value_min: 120,
            valueMaxOperator: 'more',
            valueMax: 0,
            valuePercentage: true,
            deviceType: 'tsabt',
            alarm_category: 'blownFuse',
            isRanged: false,
            hasReference: true,
          },
        ],
      },
    ],
    vtn: [
      {
        alarm_category_Id: 'powerQuality',
        alarm_category: 'ALARMS.CATEGORY.POWER_QUALITY',
        types: [
          {
            key: 'neutralOvervoltageLevel1',
            referenceValue: 0,
            referenceValueUnit: 'v',
            alarmType: 'ALARMS.CATEGORY.NEUTRAL_OVERVOLTAGE_LEVEL_1',
            duration: 1,
            durationUnit: 'hour',
            priority: 'medium',
            valueMinOperator: 'more',
            value_min: 20,
            valueMaxOperator: 'more',
            valueMax: 50,
            valuePercentage: false,
            deviceType: 'vtn',
            alarm_category: 'powerQuality',
            isRanged: true,
            hasReference: false,
          },
          {
            key: 'neutralOvervoltageLevel2',
            referenceValue: 0,
            referenceValueUnit: 'v',
            alarmType: 'ALARMS.CATEGORY.NEUTRAL_OVERVOLTAGE_LEVEL_2',
            duration: 1,
            durationUnit: 'hour',
            priority: 'high',
            valueMinOperator: 'more',
            value_min: 50,
            valueMaxOperator: 'more',
            valueMax: 0,
            valuePercentage: false,
            deviceType: 'vtn',
            alarm_category: 'powerQuality',
            isRanged: false,
            hasReference: false,
          },
          {
            key: 'overTemperature',
            referenceValue: 0,
            referenceValueUnit: 'C',
            alarmType: 'ALARMS.CATEGORY.OVERTEMPERATURE',
            duration: 1,
            durationUnit: 'hour',
            priority: 'low',
            valueMinOperator: 'moreEquals',
            value_min: 70,
            valueMaxOperator: 'more',
            valueMax: 0,
            valuePercentage: false,
            deviceType: 'vtn',
            alarm_category: 'powerQuality',
            isRanged: false,
            hasReference: false,
          },
        ],
      },
    ],
  });

  loading = signal<boolean>(false);
  visible = input<boolean>(false);
  closeDrawer = output<boolean>();
  adminAlarmForm: FormGroup;
  deviceType = input<Device>('rsabt');
  titleDevice = input<string>('');
  typeOfView = input<string>('');
  centerToEdit = input<any>('');
  infoToEdit = input<any>({});
  distributorDefault = input<any>('');
  sortedTenants = input<any>([]);
  centersByTenant = signal<CommonOption[]>([]);
  trafosByCenter = signal<CommonOption[]>([]);
  linesByTrafo = signal<SelectOption[]>([]);
  valueOperatorOptions = signal<SelectOption[]>(SYMBOLS_FILTER_OPERATORS);
  durationUnitOptions = signal<SelectOption[]>(DURATIONS_UNITS);
  referenceValueUnitsOptions = signal<SelectOption[]>(REFERENCE_VALUE_UNITS);
  priorityOptions = signal<SelectOption[]>(PRIORITY);
  listOfData = signal<any>(LIST_TABLES_DRAWER_ADMIN);
  valuePercentageOptions = signal<SelectOption[]>(VALUE_PERCENTAGE);
  initialFormValues = signal<any>({});
  hasDefaultConfig = signal<boolean>(false);
  permissions = this.permissionsService.permissions;
  openSignal = input<number>(0);

  /**
   * Returns true if the user has 'w' write permission.
   */
  hasWritePermission(): boolean {
    return this.permissions().some(p => p === 'sabt:alarm_cfg:w');
  }

  /**
   * Formats value and label pairs. If either is missing, returns the existing one without the separator.
   */
  formatPair(value: any, label: any): string {
    if (value != null && value !== '' && label != null && label !== '') {
      return `${value} - ${label}`;
    }
    if (value != null && value !== '') return `${value}`;
    if (label != null && label !== '') return `${label}`;
    return '';
  }

  /**
   * Initializes the AdministrationDrawerComponent, sets up the form group and effect for distributor and center edit logic.
   * @param fb - FormBuilder instance for creating form controls.
   */
  constructor(private fb: FormBuilder) {
    this.adminAlarmForm = this.fb.group({
      distributor: this.fb.control(''),
      center: this.fb.control(''),
      trafo: this.fb.control(''),
      line: this.fb.control(''),
      config: this.fb.array([], Validators.required)
    });

    let lastCenterId: string | undefined;
    let lastTypeOfView: string | undefined;
    let lastOpenSignal = this.openSignal();
    


    effect((onCleanup) => {
      const distributorDefault = this.distributorDefault();
      const centerToEdit = this.centerToEdit()?.id;
      const typeOfView = this.typeOfView();
      const current = this.openSignal();
      const isVisible = this.visible();
      if (current !== lastOpenSignal && isVisible) {
        lastOpenSignal = current;
        // small timeout to ensure drawer content rendered before resetting
        setTimeout(() => this.resetValues(), 0);
      }
      if (
        typeOfView === "edit_center" &&
        (centerToEdit !== lastCenterId || typeOfView !== lastTypeOfView) ||
        typeOfView === "view-saved"
      ) {
        lastCenterId = centerToEdit;
        lastTypeOfView = typeOfView;
        const debounceTimer = setTimeout(() => {
          this.getConfigSpecific(centerToEdit);
        }, 100);
        onCleanup(() => clearTimeout(debounceTimer));
      }
      if (typeOfView === "edit") {
        this.adminAlarmForm.controls['center'].errors?.['required'] ? this.adminAlarmForm.controls['center'].setErrors(null) : null
      }
    });
  }

  /**
   * Angular lifecycle hook. Initializes forms, sets the distributor value, and subscribes to distributor changes.
   */
  ngOnInit() {
    this.initializeForms();
    this.adminAlarmForm.controls['distributor'].setValue(
      this.distributorDefault(),
    );
    // Force the form control to update and mark it as touched to ensure UI sync
    this.adminAlarmForm.get('distributor')?.updateValueAndValidity();
    this.distributorChange();
  }

  /**
   * Loads the configuration for a specific center and updates the form values.
   * @param centerToEdit - The ID of the center to fetch configuration for.
   */
  getConfigSpecific(centerToEdit: string) {
    this.loading.set(true);
    this.administrationService
      .getCenterConfig(centerToEdit)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.upDateValuesForm(data);
        this.loading.set(false);
        this.initializeForms();
      });
  }

  /**
   * Returns the CSS class for a select control based on its validation state.
   * Adds a red border if the control is invalid and has been touched.
   *
   * @param controlName - The name of the control in the form group.
   * @returns The CSS class string for styling the select control.
   */
  getSelectClass(controlName: string): string {
    const control = this.adminAlarmForm.get(controlName);
    if (control?.invalid && control?.touched) {
      return 'border-red-600 rounded-sm border-2';
    }
    return '';
  }

  /**
   * Gets the center name by center ID from the centersByTenant signal.
   * @param centerId - The ID of the center to find the name for.
   * @returns The center name or empty string if not found.
   */
  private getCenterNameById(centerId: string): string {
    const center = this.centersByTenant().find(c => c.id === centerId);
    return center ? center.name : '';
  }

  /**
   * Gets the current center name based on the view type and available data.
   * @returns The center name or empty string if not available.
   */
  private getCurrentCenterName(): string {
    const typeOfView = this.typeOfView();

    if (typeOfView === 'edit_center') {
      return this.centerToEdit()?.name || this.centerToEdit()?.centerName || '';
    } else if (typeOfView === 'add_center') {
      const centerId = this.adminAlarmForm.value.center;
      return centerId ? this.getCenterNameById(centerId) : '';
    } else if (typeOfView === 'edit') {
      // For general settings edit, check if we have center info in various places
      const infoToEdit = this.infoToEdit();
      const centerToEdit = this.centerToEdit();

      // Check infoToEdit first
      if (infoToEdit?.centerName) {
        return infoToEdit.centerName;
      }

      // Check centerToEdit for centerName property (from administration table)
      if (centerToEdit?.centerName) {
        return centerToEdit.centerName;
      }

      // Check centerToEdit for name property
      if (centerToEdit?.name) {
        return centerToEdit.name;
      }

      // Fallback to form value if available
      const centerId = this.adminAlarmForm.value.center;
      return centerId ? this.getCenterNameById(centerId) : '';
    }

    return '';
  }

  /**
   * Returns the CSS class for a config control inside a FormArray based on its validation state.
   * Adds a red border if the control is invalid and has been touched.
   *
   * @param index - The index of the FormGroup in the FormArray.
   * @param controlName - The name of the control within the FormGroup.
   * @returns The CSS class string for styling the config control.
   */
  getConfigControlClass(index: number, controlName: string): string {
    const group = this.configArray.at(index) as FormGroup;
    const control = group.get(controlName);
    if (control?.invalid && control?.touched) {
      return 'border-red-600 border-2';
    }
    return '';
  }

  /**
   * Checks if the form is invalid or any config input is empty.
   * @returns True if the form is invalid or any config input is empty.
   */
  isFormInvalid(): boolean {
    if (this.adminAlarmForm.invalid) return true;
    // Check if any config input is empty
    const configArray = this.configArray;
    for (let i = 0; i < configArray.length; i++) {
      const group = configArray.at(i) as FormGroup;
      for (const key of Object.keys(group.controls)) {
        if (
          group.controls[key].validator && // Only check required fields
          group.controls[key].hasError('required')
        ) {
          return true;
        }
        // Also check for empty string or null
        if (
          group.controls[key].value === '' ||
          group.controls[key].value === null ||
          group.controls[key].value === undefined
        ) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Returns the FormArray for the alarm configuration.
   * @returns The 'config' FormArray from the adminAlarmForm.
   */
  get configArray(): FormArray {
    return this.adminAlarmForm.get('config') as FormArray;
  }

  /**
   * Clears and initializes the alarm configuration form array based on the current device type data.
   */
  initializeForms() {
    this.configArray.clear();
    const deviceData = this.listOfData()[this.deviceType()];
    if (deviceData) {
      deviceData.forEach((alarmCategory: any) => {
        alarmCategory.types.forEach((type: any) => {
          this.configArray.push(this.createRowForm(type));
        });
      });
    }
  }

  /**
   * Creates a FormGroup for a single alarm configuration row.
   * @param type - The alarm type object containing configuration properties.
   * @returns A FormGroup with controls for the alarm configuration.
   */
  createRowForm(type: any): FormGroup {
    return this.fb.group({
      alarmCategory: [type.alarm_category, []],
      alarmType: [type.key, []],
      priority: [type.priority, [Validators.required]],
      duration: [type.duration, [Validators.required]],
      durationUnit: [type.durationUnit, [Validators.required]],
      referenceValue: [type.referenceValue, [Validators.required]],
      referenceValueUnit: [type.referenceValueUnit, [Validators.required]],
      value_min: [type.value_min, [Validators.required]],
      valueMinOperator: [type.valueMinOperator, [Validators.required]],
      valueMax: [type.valueMax, [Validators.required]],
      valueMaxOperator: [type.valueMaxOperator, [Validators.required]],
      valuePercentage: [type.valuePercentage, [Validators.required]],
      isRanged: [type.isRanged],
      hasReference: [type.hasReference],
    });
  }

  /**
   * Emits an event to close the drawer.
   */
  close(): void {
    this.closeDrawer.emit(false);
  }

  /**
   * Filters the configuration array to only include items matching the current device type.
   * @returns Array of configuration items for the current device type only.
   */
  private filterConfigByDeviceType(): any[] {
    const currentDeviceType = this.deviceType();
    const allConfig = this.adminAlarmForm.value.config || [];

    // Get the alarm types for the current device type
    const deviceData = this.listOfData()[currentDeviceType];
    if (!deviceData) {
      return [];
    }

    // Create a Set of valid alarm types for the current device
    const validAlarmTypes = new Set<string>();
    deviceData.forEach((alarmCategory: any) => {
      alarmCategory.types.forEach((type: any) => {
        validAlarmTypes.add(type.key);
      });
    });

    // Filter config to only include items for the current device type
    return allConfig.filter((configItem: any) =>
      validAlarmTypes.has(configItem.alarmType)
    );
  }

  /**
   * Handles the save action for a new center configuration.
   * Submits the form data to the API and updates the form state.
   */
  onSubmitSaveCenter() {
    if (this.adminAlarmForm.valid) {
      const body = {
        distributorId: this.adminAlarmForm.value.distributor,
        centerId: this.adminAlarmForm.value.center,
        transformerId: this.adminAlarmForm.value.trafo,
        lineId: this.adminAlarmForm.value.line,
        deviceType: this.deviceType(),
        config: this.filterConfigByDeviceType(),
      };
      this.administrationService
        .saveCenterConfig(body)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data) => {
            // Show success notification
            const centerName = this.getCurrentCenterName();
            this.notification.create(
              'success',
              this.transloco.translate('NOTIFICATIONS.ADMINISTRATION.TITLE'),
              this.transloco.translate('NOTIFICATIONS.ADMINISTRATION.CENTER_CONFIG.ADD_SUCCESS', { centerName }),
              {
                nzPlacement: 'bottomRight',
              },
            );
            this.upDateValuesForm(data);
            this.loading.set(false);
            this.close();
          },
          error: (error) => {
            // Let the interceptor handle error notifications
            console.error('Error saving center configuration:', error);
            this.loading.set(false);
          },
        });
    } else {
      console.log('Formulario inválido');
    }
  }

  /**
   * Handles the update action for an existing center configuration.
   * Submits the updated config to the API and closes the drawer.
   */
  onSubmitChangeSaveCenter() {
    if (this.adminAlarmForm.valid) {
      const body: bodyToConfig = {
        config: this.filterConfigByDeviceType(),
      };
      this.administrationService
        .updateSavedCenterConfig(body, this.centerToEdit().id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data) => {
            // Show success notification
            const centerName = this.getCurrentCenterName();
            this.notification.create(
              'success',
              this.transloco.translate('NOTIFICATIONS.ADMINISTRATION.TITLE'),
              this.transloco.translate('NOTIFICATIONS.ADMINISTRATION.CENTER_CONFIG.SAVE_SUCCESS', { centerName }),
              {
                nzPlacement: 'bottomRight',
              },
            );
            this.loading.set(false);
            this.close();
          },
          error: (error) => {
            // Let the interceptor handle error notifications
            console.error('Error updating center configuration:', error);
            this.loading.set(false);
          },
        });
    } else {
      console.log('Formulario inválido');
    }
  }

  /**
   * Handles saving the general distributor configuration.
   * Submits the config to the API and closes the drawer.
   */
  onSubmitDistributorDefault() {
    if (this.adminAlarmForm.valid) {
      const body: bodyToConfig = {
        config: this.filterConfigByDeviceType(),
      };
      this.administrationService
        .updateDataDistributorConfig(
          this.deviceType(),
          this.adminAlarmForm.value.distributor,
          body,
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data) => {
            // Show success notification
            const centerName = this.getCurrentCenterName();
            this.notification.create(
              'success',
              this.transloco.translate('NOTIFICATIONS.ADMINISTRATION.TITLE'),
              this.transloco.translate('NOTIFICATIONS.ADMINISTRATION.CENTER_CONFIG.SAVE_SUCCESS', { centerName }),
              {
                nzPlacement: 'bottomRight',
              },
            );
            this.loading.set(false);
            this.close();
          },
          error: (error) => {
            // Let the interceptor handle error notifications
            console.error('Error saving distributor configuration:', error);
            this.loading.set(false);
          },
        });
    } else {
      console.log('Formulario inválido');
    }
  }

  /**
   * Handles adding a default general distributor configuration.
   * Submits the config to the API and closes the drawer.
   */
  onSubmitAddDistributorDefault() {
    if (this.adminAlarmForm.valid) {
      const body: any = {
        distributorId: this.adminAlarmForm.value.distributor,
        deviceType: this.deviceType(),
        config: this.filterConfigByDeviceType(),
      };
      this.administrationService
        .addDataDistributorConfig(body)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data) => {
            // Show success notification
            const centerName = this.getCurrentCenterName();
            this.notification.create(
              'success',
              this.transloco.translate('NOTIFICATIONS.ADMINISTRATION.TITLE'),
              this.transloco.translate('NOTIFICATIONS.ADMINISTRATION.CENTER_CONFIG.SAVE_SUCCESS', { centerName }),
              {
                nzPlacement: 'bottomRight',
              },
            );
            this.loading.set(false);
            this.close();
          },
          error: (error) => {
            // Let the interceptor handle error notifications
            console.error('Error adding distributor configuration:', error);
            this.loading.set(false);
          },
        });
    } else {
      console.log('Formulario inválido');
    }
  }

  /**
   * Resets the form values to the initial state and triggers distributor change logic.
   */
  resetValues() {
    this.upDateValuesForm(this.initialFormValues());
    this.distributorChange();
  }

  /**
   * Dispatches the submit event for the admin alarm form and executes the corresponding action.
   *
   * @param action - The action to perform. Accepted values:
   *   - 'saveCenter': Save configuration for a new center.
   *   - 'editCenter': Edit configuration for an existing center.
   *   - 'saveGeneralSettings': Save general distributor configuration.
   *   - 'addGeneralSettings': Add default general distributor configuration.
   *
   * This method ensures the form is submitted (triggering validation and bindings)
   * before executing the requested action handler.
   */
  submitForm(action: string) {
    const form = document.getElementById('adminAlarmForm') as HTMLFormElement;
    if (form) {
      form.dispatchEvent(new Event('submit'));
    }
    if (action === 'saveCenter') {
      this.onSubmitSaveCenter();
    }
    if (action === 'editCenter') {
      this.onSubmitChangeSaveCenter();
    }
    if (action === 'saveGeneralSettings' || action === 'addGeneralSettings') {
      this.modal.confirm({
        nzTitle: this.transloco.translate('ADMIN.MODAL_CONFIRM.TITLE'),
        nzContent: this.transloco.translate('ADMIN.MODAL_CONFIRM.CONTENT'),
        nzCentered: true,
        nzOkText: this.transloco.translate('ADMIN.MODAL_CONFIRM.OK'),
        nzCancelText: this.transloco.translate('ADMIN.MODAL_CONFIRM.CANCEL'),
        nzOnOk: () => {
          if (action === 'saveGeneralSettings') {
            this.onSubmitDistributorDefault();
          }
          if (action === 'addGeneralSettings') {
            this.onSubmitAddDistributorDefault();
          }
        },
      });
    }
  }

  /**
   * Handles changes to the distributor selection.
   * Resets related form controls and loads configuration and centers for the selected distributor.
   */
  distributorChange() {
    this.centersByTenant.set([]);
    this.linesByTrafo.set([]);
    this.trafosByCenter.set([]);

    // Reset form controls and force UI update
    this.adminAlarmForm.patchValue({
      center: '',
      trafo: '',
      line: ''
    });

    // Force form controls to update and mark as touched to trigger validation
    this.adminAlarmForm.get('center')?.updateValueAndValidity();
    this.adminAlarmForm.get('trafo')?.updateValueAndValidity();
    this.adminAlarmForm.get('line')?.updateValueAndValidity();

    if (this.typeOfView() !== 'edit_center' && this.typeOfView() !== 'view-saved') {
      this.loading.set(true);

      const config$ = this.administrationService
        .getDataDistributorConfig(
          this.deviceType(),
          this.adminAlarmForm.controls['distributor'].value,
        )
        .pipe(
          catchError(() => {
            const defaultClone = JSON.parse(JSON.stringify(this.listDefault));
            this.listOfData.set(defaultClone);
            this.hasDefaultConfig.set(false);
            return of(null);
          }),
          takeUntilDestroyed(this.destroyRef),
        );

      const centers$ = this.administrationService
        .getCenters(this.adminAlarmForm.controls['distributor'].value)
        .pipe(takeUntilDestroyed(this.destroyRef));

      forkJoin([config$, centers$]).subscribe(([data, centers]) => {
        if (data) {
          this.hasDefaultConfig.set(true);
          this.initialFormValues.set(data);
          this.upDateValuesForm(data);
          // Reinitialize forms after updating the data to recreate form controls
          this.initializeForms();
        } else {
          // If no config data from API, reinitialize forms with default data
          this.hasDefaultConfig.set(false);
          this.initializeForms();
        }
        this.centersByTenant.set(centers);
        this.loading.set(false);
      });
    }
  }

  /**
   * Updates the alarm configuration values in the form based on the provided data.
   * @param data - The configuration data to update the form with.
   */
  upDateValuesForm(data: any) {
    if (data.config) {
      const filteredAlarms = this.listOfData()[data.deviceType !== '' ? data.deviceType : this.deviceType()].map(
        (alarm: any) => {
          data.config.forEach((config: any) => {
            if (alarm.alarm_category_Id.includes(config.alarmCategory)) {
              alarm.types.map((type: any) => {
                if (type.key === config.alarmType) {
                  {
                    type.priority = config.priority;
                    type.duration = config.duration;
                    type.durationUnit = config.durationUnit;
                    type.referenceValue = config.referenceValue;
                    type.referenceValueUnit = config.referenceValueUnit;
                    type.value_min = config.value_min;
                    type.valueMinOperator = config.valueMinOperator;
                    type.valueMax = config.valueMax;
                    type.valueMaxOperator = config.valueMaxOperator;
                    type.valuePercentage = config.valuePercentage;
                    type.isRanged = config.isRanged;
                    type.hasReference = config.hasReference;
                  }
                }
              });
            }
          });
          return alarm;
        },
      );
      this.listOfData.set({
        ...this.listOfData(),
        [data.deviceType]: filteredAlarms,
      });
    }
  }

  /**
   * Handles changes to the center selection.
   * Loads transformers and resets related form controls for the selected center.
   */
  centerChange() {
    this.loading.set(true);
    if (this.centersByTenant().length === 0) {
      this.loading.set(false);
      return;
    }
    this.trafosByCenter.set([]);
    this.linesByTrafo.set([]);

    // Reset form controls and force UI update
    this.adminAlarmForm.patchValue({
      trafo: '',
      line: ''
    });

    // Force form controls to update
    this.adminAlarmForm.get('trafo')?.updateValueAndValidity();
    this.adminAlarmForm.get('line')?.updateValueAndValidity();

    this.administrationService
      .getTrafos(this.adminAlarmForm.controls['center'].value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.trafosByCenter.set(data);
        this.loading.set(false);
      });
  }

  /**
   * Handles changes to the transformer selection.
   * Loads lines and resets related form controls for the selected transformer.
   */
  trafoChange() {
    this.loading.set(true);
    this.linesByTrafo.set([]);

    // Reset form controls and force UI update
    this.adminAlarmForm.patchValue({
      line: ''
    });

    // Force form control to update
    this.adminAlarmForm.get('line')?.updateValueAndValidity();

    if (this.trafosByCenter().length === 0) {
      this.loading.set(false);
      return;
    }
    if (this.deviceType() !== 'tsabt') {
      this.loading.set(false);
      return;
    }
    this.administrationService
      .getLines(
        this.adminAlarmForm.controls['center'].value,
        this.adminAlarmForm.controls['trafo'].value,
      )
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data) => {
        this.linesByTrafo.set(data);
        this.loading.set(false);
      });
  }

  // Prevent typing a dot as decimal separator
  preventDot(event: KeyboardEvent): void {
    if (event.key === '.') {
      event.preventDefault();
    }
  }

  // Handle paste: replace dots with commas then update control
  onPasteNumeric(event: ClipboardEvent, index: number, controlName: string): void {
    const clipboard = event.clipboardData?.getData('text') ?? '';
    const sanitized = clipboard.replace(/\./g, ',');
    // prevent default paste and insert sanitized text
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    input.value = input.value.slice(0, start) + sanitized + input.value.slice(end);
    // trigger input handler to update control
    this.onNumericInput({ target: input } as any, index, controlName);
  }

  // On input: show comma visually, parse value to number and set FormControl (no emit to avoid loops)
  onNumericInput(event: Event, index: number, controlName: string): void {
    const input = event.target as HTMLInputElement;
    if (!input) return;
    // replace any dots with commas for visual consistency
    const visual = input.value.replace(/\./g, ',');
    if (input.value !== visual) {
      input.value = visual;
    }
    // convert comma to dot for parsing numeric value
    const normalized = visual.replace(/,/g, '.').trim();
    const parsed = normalized === '' ? null : Number(normalized);
    const group = this.configArray.at(index) as FormGroup;
    const control = group?.get(controlName);
    if (control) {
      control.setValue(parsed, { emitEvent: false });
    }
  }
}
